import { useState, useEffect, useCallback } from "react";
import axios from "axios";

import {
  Spinner,
  Row,
  Col,
  Nav,
  Container,
  Modal,
  Button,
} from "react-bootstrap";

import { useParams, useNavigate } from "react-router-dom";

import { FaChevronDown, FaChevronUp, FaEye } from "react-icons/fa";
import { IoArrowBack } from "react-icons/io5";

import { toast } from "react-toastify";
import { TUTOR_ASSIGNMENT_API, ADMIN_COURSE_API } from "../../../api/api";


import Announcementsimg from "../../../assets/announcements-empty.png";
import Thumbnail from "../../../assets/admin-course-moduel-vides.png";
import assignThubnail from "../../../assets/student/student-assignments.png";

import VideoIcon from "../../../assets/icons/student/video.svg";

import { handleApiError } from "../../../utils/apiErrorHandler";

import "./AdminPublishedCourseManagement.css";

const AdminPublishedCourseManagement = () => {
  const navigate = useNavigate();
  const { courseId } = useParams();

  const [activeTab, setActiveTab] = useState({});
  const [publishedCourses, setPublishedCourses] = useState([]);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  //toggle open
  const [openModuleId, setOpenModuleId] = useState(null);
  const [openAssignmentId, setOpenAssignmentId] = useState(null);

  // video view and assignment view
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [assignmentData, setAssignmentData] = useState(null);

  //Modal
  const [isAdminReviewModalOpen, setIsAdminReviewModalOpen] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);

  //loading
  const [isLoadingCourses, setIsLoadingCourses] = useState(true);

  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  const fetchPublishedCourses = useCallback(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      setIsLoadingCourses(false);
      navigate("/admin-signin");
      return;
    }

    try {
      let response;
      if (courseId) {
        // Fetch single course
        response = await axios.post(
          ADMIN_COURSE_API.GET_ADMIN_COURSE_WITH_STATUS_ID,
          { status: "Published", course_id: courseId },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } else {
        // Fetch all published courses
        response = await axios.post(
          ADMIN_COURSE_API.GET_ADMIN_TOTAL_COURSE_WITH_STATUS,
          { status: "Published" },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }

      const data = response.data;

      if (data.statusCode === 200 && data.data.length > 0) {
        const normalizedCourses = data.data.map((courseData) => ({
          courseId: courseData.course_id,
          title: courseData.course_title || "N/A",
          tutor: courseData.tutor || "N/A",
          modules: courseData.total_modules || "N/A",
          duration: courseData.duration || "N/A",
          enrolled: courseData.enrolled_students || "-",
          category: courseData.category || "N/A",
          level: courseData.level || "N/A",
          description: courseData.description || "N/A",
          status: courseData.status || "N/A",
          modulesData: (courseData.modules || []).map((mod) => ({
            moduleId: mod.module_id,
            moduleTitle: mod.module_title || "N/A",
            moduleStatus: mod.status || "N/A",
            videos: (mod.videos || []).map((vid) => ({
              moduleVideoId: vid.module_video_id,
              videoTitle: vid.video_title || "N/A",
              videoStatus: vid.status || "N/A",
            })),
            assignments: (mod.assignments || []).map((a) => ({
              assignmentId: a.assignment_id,
              assignmentTitle: a.title,
              assignmentStatus: a.status,
            })),
          })),
        }));

        setPublishedCourses(normalizedCourses);
        // initialize tabs for all courses
        const initialTabs = {};
        normalizedCourses.forEach((c) => (initialTabs[c.courseId] = "details"));
        setActiveTab(initialTabs);
      } else {
        showErrorToast(data.message);
      }
    } catch (err) {
      console.error(err);

      // CHECK (expiry / invalid token)
      handleApiError(err, navigate, showErrorToast, "/admin-signin");
    } finally {
      setIsLoadingCourses(false);
    }
  }, [courseId, navigate]);

  useEffect(() => {
    fetchPublishedCourses();
  }, [fetchPublishedCourses]);

  const handleOpenVideo = async (moduleVideoId) => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/admin-signin");
      return;
    }

    try {
      const response = await axios.post(
        ADMIN_COURSE_API.GET_MODULE_VIDEO_BY_ID,
        { module_video_id: moduleVideoId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        setSelectedVideo(data.data);
        setIsAdminReviewModalOpen(true);
      } else {
        showErrorToast(data.message);
      }
    } catch (err) {
      console.error(err);

      // CHECK (expiry / invalid token)
      handleApiError(err, navigate, showErrorToast, "/admin-signin");
    }
  };

  const handleViewAssignment = async (assignmentId) => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/admin-signin");
      return;
    }

    try {
      const response = await axios.post(
        TUTOR_ASSIGNMENT_API.GET_ASSIGNMENT_BY_ID,
        { assignment_id: assignmentId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        setAssignmentData(data.assignment);
        setCurrentQuestionIndex(0);
        setShowAssignmentModal(true);
      } else {
        showErrorToast(data.message);
      }
    } catch (err) {
      console.error(err);

      // CHECK (expiry / invalid token)
      handleApiError(err, navigate, showErrorToast, "/admin-signin");
    }
  };

  const handleClose = () => {
    setSelectedVideo(null);
    setIsAdminReviewModalOpen(false);
  };

  const toggleModule = (moduleId) => {
    setOpenModuleId((prev) => (prev === moduleId ? null : moduleId));
  };

  const toggleAssignment = (assignmentId) => {
    setOpenAssignmentId((prev) =>
      prev === assignmentId ? null : assignmentId,
    );
  };

  const assignmentQuestions = assignmentData?.questions || [];
  const currentQuestion = assignmentQuestions[currentQuestionIndex];
  const isLastQuestion =
    currentQuestionIndex === assignmentQuestions.length - 1;

  return (
    <Container className="admin-published-course-container">
      <div className="admin-pending-backmoving-button-wrapper">
        <button
          className="admin-pending-backmoving-button-main"
          onClick={() => navigate("/header/admin/all-courses")}
        >
          <IoArrowBack className="admin-pending-backmoving-button-icon" />
          <span className="admin-pending-backmoving-button-text">Back</span>
        </button>
      </div>
      <h4 className="admin-dashboard-title">Published Courses</h4>
      <p className="admin-dashboard-subtitle">
       View published courses, available modules, and assignment details. 
      </p>

      {isLoadingCourses ? (
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : publishedCourses.length === 0 ? (
        <div className="tutor-assignment-empty-box">
          <div className="tutor-assignment-empty-content text-center mt-5">
            <img
              src={Announcementsimg}
              alt="empty"
              className="tutor-announcements-section-empty-img mb-3"
            />
            <h5>No published course found.</h5>
          </div>
        </div>
      ) : (
        publishedCourses.map((course) => (
          <div
            key={course.courseId}
            className="admin-published-course-header-container mb-4"
          >
            <div className="admin-published-course-header">
              <h5 className="admin-published-course-title">
                {course.title}
                <p
                  className={`admin-published-course-badge admin-published-course-status-${course.status?.toLowerCase()}`}
                >
                  {course.status}
                </p>
              </h5>
              <p className="admin-published-course-subtitle">
                View published courses along with tutor details, duration, enrolled students, modules, and assignments.
              </p>
            </div>

            {/* TABS */}
            <Row className="justify-content-start">
              <Col lg={4} md={8} sm={12}>
                <Nav variant="tabs" className="admin-published-course-tabs">
                  <Nav.Item>
                    <Nav.Link
                      active={activeTab[course.courseId] === "details"}
                      onClick={() =>
                        setActiveTab((prev) => ({
                          ...prev,
                          [course.courseId]: "details",
                        }))
                      }
                    >
                      Course Details
                    </Nav.Link>
                  </Nav.Item>

                  <Nav.Item>
                    <Nav.Link
                      active={activeTab[course.courseId] === "modules"}
                      onClick={() =>
                        setActiveTab((prev) => ({
                          ...prev,
                          [course.courseId]: "modules",
                        }))
                      }
                    >
                      Modules
                    </Nav.Link>
                  </Nav.Item>

                  <Nav.Item>
                    <Nav.Link
                      active={activeTab[course.courseId] === "assignments"}
                      onClick={() =>
                        setActiveTab((prev) => ({
                          ...prev,
                          [course.courseId]: "assignments",
                        }))
                      }
                    >
                      Assignments
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
            </Row>

            {/* Details Tab */}
            {activeTab[course.courseId] === "details" && (
              <div className="admin-published-course-content">
                <div className="admin-published-course-info-grid">
                  <div className="admin-published-course-info-box">
                    <p>Tutor</p>
                    <h6>{course.tutor}</h6>
                  </div>
                  <div className="admin-published-course-info-box">
                    <p>Modules</p>
                    <h6>{course.modules}</h6>
                  </div>
                  <div className="admin-published-course-info-box">
                    <p>Duration</p>
                    <h6>{course.duration}</h6>
                  </div>
                  <div className="admin-published-course-info-box">
                    <p>Enrolled Students</p>
                    <h6>{course.enrolled}</h6>
                  </div>
                </div>

                <div className="admin-published-course-bottom">
                  <Col md={6}>
                    <p>Category</p>
                    <h6>{course.category}</h6>
                  </Col>
                  <Col md={6}>
                    <p>Level</p>
                    <h6>{course.level}</h6>
                  </Col>
                </div>

                <div className="admin-published-course-description">
                  <p>Description</p>
                  <span>{course.description}</span>
                </div>
              </div>
            )}

            {/* MODULES TAB */}
            {activeTab[course.courseId] === "modules" && (
              <div className="admin-published-course-modules">
                {course.modulesData?.length === 0 ? (
                  <div className="tutor-mycourse-module-emptypart-wrapper">
                    <div className="tutor-mycourse-module-emptypart-container">
                      <img
                        src={Announcementsimg}
                        alt="empty"
                        className="tutor-announcements-section-empty-img mb-3"
                      />
                      <p className="tutor-mycourse-module-emptypart-title">
                        No published course Modules found.
                      </p>
                    </div>
                  </div>
                ) : (
                  course.modulesData.map((module, index) => {
                    const isOpen = openModuleId === module.moduleId;

                    return (
                      <div
                        key={module.moduleId}
                        className="admin-published-course-module-card"
                      >
                        {/* MODULE HEADER */}
                        <div
                          className="admin-published-course-module-header"
                          onClick={() => toggleModule(module.moduleId)}
                        >
                          <h6 className="admin-published-course-module-title">
                            <span className="admin-published-course-module-id">
                              {index + 1}
                            </span>
                            <span className="admin-published-course-module-name">
                              {module.moduleTitle}
                            </span>
                          </h6>

                          <div className="admin-published-course-module-statu-both">
                            <span
                              className={`admin-published-course-module-status admin-published-course-status-${module.moduleStatus?.toLowerCase()}`}
                            >
                              {module.moduleStatus}
                            </span>
                            <span className="admin-published-course-module-arrow">
                              {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                            </span>
                          </div>
                        </div>

                        {/* VIDEOS */}
                        {isOpen && (
                          <div className="admin-published-course-module-videos">
                            {module.videos.map((video) => (
                              <div
                                key={video.moduleVideoId}
                                className="admin-published-course-video-row"
                              >
                                <div className="admin-published-course-video-both-set">
                                  <img
                                    src={Thumbnail}
                                    alt={video.videoTitle}
                                    className="admin-published-course-video-rowimageee"
                                  />

                                  <span className="admin-published-course-video-title">
                                    {video.videoTitle}
                                  </span>
                                </div>

                                <div className="admin-published-course-video-actions">
                                  <span
                                    className={`admin-published-course-module-status admin-published-course-status-${video.videoStatus?.toLowerCase()}`}
                                  >
                                    {video.videoStatus}
                                  </span>

                                  <button
                                    className="admin-published-course-play-btn"
                                    onClick={() =>
                                      handleOpenVideo(video.moduleVideoId)
                                    }
                                  >
                                    <img
                                      src={VideoIcon}
                                      alt="video"
                                      className="admin-published-course-lesson-icon"
                                    />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* ASSIGNMENTS TAB */}
            {activeTab[course.courseId] === "assignments" && (
              <div className="admin-published-course-modules">
                {course.modulesData.every(
                  (module) =>
                    !module.assignments || module.assignments.length === 0,
                ) ? (
                  <div className="tutor-mycourse-module-emptypart-wrapper">
                    <div className="tutor-mycourse-module-emptypart-container">
                      <img
                        src={Announcementsimg}
                        alt="empty"
                        className="tutor-announcements-section-empty-img mb-3"
                      />
                      <p className="tutor-mycourse-module-emptypart-title">
                        No published assignments found.
                      </p>
                    </div>
                  </div>
                ) : (
                  course.modulesData.map((module, index) =>
                    module.assignments?.map((assignment) => {
                      const isOpen =
                        openAssignmentId === assignment.assignmentId;

                      return (
                        <div
                          key={assignment.assignmentId}
                          className="admin-published-course-module-card"
                        >
                          {/* ASSIGNMENT HEADER (like module header) */}
                          <div
                            className="admin-published-course-module-header"
                            onClick={() =>
                              toggleAssignment(assignment.assignmentId)
                            }
                          >
                            <h6 className="admin-published-course-module-title">
                              <span className="admin-published-course-module-id">
                                {index + 1}
                              </span>
                              <span className="admin-published-course-module-name">
                                {module.moduleTitle}
                              </span>
                            </h6>

                            <div className="admin-published-course-module-statu-both">
                              <span
                                className={`admin-published-course-module-status admin-published-course-status-${assignment.assignmentStatus?.toLowerCase()}`}
                              >
                                {assignment.assignmentStatus}
                              </span>
                              <span className="admin-published-course-module-arrow">
                                {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                              </span>
                            </div>
                          </div>

                          {/* ASSIGNMENT BODY */}
                          {isOpen && (
                            <div className="admin-published-course-module-videos">
                              <div className="admin-published-course-video-row">
                                <div className="admin-published-course-video-both-set">
                                  <img
                                    src={assignThubnail}
                                    alt={assignment.assignmentTitle}
                                    className="admin-published-course-video-rowimageee"
                                  />

                                  <span className="admin-published-course-video-title">
                                    {assignment.assignmentTitle}
                                  </span>
                                </div>
                                <div className="admin-published-course-video-actions">
                                  <span
                                    className={`admin-published-course-module-status admin-published-course-status-${assignment.assignmentStatus?.toLowerCase()}`}
                                  >
                                    {assignment.assignmentStatus}
                                  </span>

                                  <FaEye
                                    className="admin-pending-course-view-icon"
                                    title="View Assignment"
                                    onClick={() =>
                                      handleViewAssignment(
                                        assignment.assignmentId,
                                      )
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }),
                  )
                )}
              </div>
            )}
          </div>
        ))
      )}

      {/* VIDEO MODAL */}
      <Modal
        show={isAdminReviewModalOpen}
        onHide={handleClose}
        size="lg"
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="admin-published-course-modal"
      >
        <div className="admin-pending-course-modal-content">
          <Modal.Header
            closeButton
            className="admin-published-course-modal-header"
          ></Modal.Header>

          <Modal.Body className="admin-published-course-modal-body">
            {selectedVideo && (
              <>
                <div className="admin-published-course-modal-video-section">
                  <video
                    className="admin-published-course-modal-video"
                    controls
                    controlsList="nodownload"
                    // noplaybackrate
                    disablePictureInPicture
                    onContextMenu={(e) => e.preventDefault()}
                  >
                    <source src={selectedVideo.video_url} type="video/mp4" />
                  </video>
                </div>
              </>
            )}
          </Modal.Body>
        </div>
      </Modal>

      {/* ASSIGNMENT REVIEW MODAL */}
      <Modal
        show={showAssignmentModal}
        onHide={() => setShowAssignmentModal(false)}
        size="lg"
        centered
        backdrop="static"
        keyboard={false}
        className="admin-pending-course-view-assignment-modal p-0"
      >
        <Modal.Header
          closeButton
          className="admin-pending-course-view-assignment-modal-header"
        >
          <div className="admin-pending-course-view-assignment-header-left">
            <h5 className="admin-pending-course-view-assignment-title">
              {assignmentData?.assignment_title}
            </h5>
            <p className="admin-pending-course-view-assignment-subtitle">
              Assignment •{" "}
              <span className="admin-pending-course-view-assignment-span">
                {assignmentQuestions.length} Questions
              </span>
            </p>
          </div>

          <div className="admin-pending-course-view-assignment-header-right">
            {String(currentQuestionIndex + 1).padStart(2, "0")} /{" "}
            {assignmentQuestions.length} Questions
          </div>
        </Modal.Header>

        <Modal.Body className="admin-pending-course-view-assignment-modal-body">
          {currentQuestion && (
            <div className="admin-pending-course-view-assignment-question-card">
              <div className="admin-pending-course-view-assignment-question-header">
                <span className="admin-pending-course-view-assignment-question-number">
                  {currentQuestionIndex + 1}.
                </span>
                <span className="admin-pending-course-view-assignment-question-text">
                  {currentQuestion.question}
                </span>

                <span className="admin-pending-course-view-assignment-question-mark">
                  1 Mark
                </span>
              </div>

              {/* OPTIONS */}
              <div className="admin-pending-course-view-assignment-options">
                {["a", "b", "c", "d"].map((key) => {
                  const isCorrect = currentQuestion.answer === key;

                  return (
                    <label
                      key={key}
                      className={`admin-pending-course-view-assignment-option ${
                        isCorrect ? "correct" : ""
                      }`}
                    >
                      <input type="radio" checked={isCorrect} readOnly />
                      <span>{currentQuestion[key]}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="admin-pending-course-view-assignment-modal-footer">
          {currentQuestionIndex > 0 ? (
            <Button
              className="admin-pending-course-view-assignment-modal-back"
              onClick={() => setCurrentQuestionIndex((prev) => prev - 1)}
            >
              Back
            </Button>
          ) : (
            <div />
          )}

          {!isLastQuestion ? (
            <Button
              className="admin-pending-course-view-assignment-modal-next"
              onClick={() => setCurrentQuestionIndex((p) => p + 1)}
            >
              Next
            </Button>
          ) : (
            <div />
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminPublishedCourseManagement;
